import React, { useState, useEffect } from 'react';
import {
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TableSortLabel, Paper, Button, TablePagination, Select, MenuItem,
  FormControl, InputLabel, Checkbox, IconButton, Popover, List,
  ListItem, ListItemIcon, ListItemText
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Link } from 'react-router-dom';
import LoadingError from './LoadingError';
import axios from '../utils/axiosInstance';
import Layout from './Layout';

const filterableColumns = ["340B ID", "CE Name", "Risk Type", "SOW", "State", "Wave"];

const OrgsTable = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  const [orderBy, setOrderBy] = useState('');
  const [sortedData, setSortedData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState({
    Manufacturer: '',
    "340B ID": '',
    "CE Name": '',
    "Risk Type": '',
    "SOW": '',
    "State": '',
    "Wave": ''
  });
  const [visibleColumns, setVisibleColumns] = useState(new Set());
  const [columnOrder, setColumnOrder] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL;

  const hasManufacturer = data.length > 0 && Object.keys(data[0]).includes("Manufacturer");

  useEffect(() => {
    const fetchData = async () => {
      try {

        const response = await axios.get(`${apiUrl}/tables/orgs`, {
          withCredentials: true,
        });
        
        setData(response.data);
        setSortedData(response.data);
        setLoading(false);
        
        const dynamicColumnOrder = [
          ...(response.data[0]?.Manufacturer ? ["Manufacturer"] : []),
          ...filterableColumns
        ];
        setColumnOrder(dynamicColumnOrder);
        setVisibleColumns(new Set(dynamicColumnOrder));
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const applyFilters = (dataToFilter, currentFilters) => {
    return dataToFilter.filter(row => 
      Object.keys(currentFilters).every(key => 
        !currentFilters[key] || row[key] === currentFilters[key]
      )
    );
  };

  const handleSort = (property) => {
    let direction = 'asc';
    if (orderBy === property) {
      if (sortDirection === 'asc') {
        direction = 'desc';
      } else {
        // Reset to default unsorted state
        setSortDirection('asc');
        setOrderBy('');
        setSortedData(applyFilters(data, filters));
        return;
      }
    }

    const sorted = [...sortedData].sort((a, b) => {
      if (a[property] < b[property]) return direction === 'asc' ? -1 : 1;
      if (a[property] > b[property]) return direction === 'asc' ? 1 : -1;
      return 0;
    });

    setSortDirection(direction);
    setOrderBy(property);
    setSortedData(sorted);
  };

  const handleFilterChange = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
    setSortedData(applyFilters(data, newFilters));
  };

  const getDistinctValues = (column) => {
    const filteredData = applyFilters(data, {...filters, [column]: ''});
    return [...new Set(filteredData.map(item => item[column]).filter(Boolean))].sort();
  };

  const handleToggleColumn = (column) => {
    const newVisible = new Set(visibleColumns);
    newVisible.has(column) ? newVisible.delete(column) : newVisible.add(column);
    setVisibleColumns(newVisible);
  };

  const handleMoveColumn = (column, direction) => {
    const currentIndex = columnOrder.indexOf(column);
    const newOrder = [...columnOrder];
    
    if (direction === 'up' && currentIndex > 0) {
      [newOrder[currentIndex - 1], newOrder[currentIndex]] = 
      [newOrder[currentIndex], newOrder[currentIndex - 1]];
    }
    
    if (direction === 'down' && currentIndex < columnOrder.length - 1) {
      [newOrder[currentIndex + 1], newOrder[currentIndex]] = 
      [newOrder[currentIndex], newOrder[currentIndex + 1]];
    }
    
    setColumnOrder(newOrder);
  };

  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const visibleCount = Array.from(visibleColumns).length;
  const columnWidth = visibleCount > 0 ? `calc((100% - 150px) / ${visibleCount})` : '0%';

  return (
    
    <Layout role={null}>
        {loading || error ? (
          <LoadingError isLoading={loading} error={error} />
        ) : (
          <Box sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            p: 2,
            width: '100%',
          }}>
        {/* Combined Filters and Settings Row */}
        <Box sx={{ 
          display: 'flex',
          gap: 2,
          alignItems: 'center',
          zIndex: 2,
          position: 'sticky',
          top: 0,
          bgcolor: 'background.paper',
          py: 1
        }}>
          {/* Filters Container */}
          <Box sx={{ 
            display: 'flex',
            gap: 2,
            flexWrap: 'wrap',
            flex: 1,
          }}>
            {columnOrder
              .filter(key => visibleColumns.has(key))
              .map((key) => (
                <FormControl key={key} sx={{ flex: '1 1 200px', minWidth: 150 }}>
                  <InputLabel>{key}</InputLabel>
                  <Select
                    value={filters[key] || ''}
                    onChange={(e) => handleFilterChange(key, e.target.value)}
                    label={key}
                  >
                    <MenuItem value=""><em>None</em></MenuItem>
                    {getDistinctValues(key).map(value => (
                      <MenuItem key={value} value={value}>{value}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ))}
          </Box>
  
          {/* Settings Icon */}
          <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
            <SettingsIcon />
          </IconButton>
        </Box>

        {/* Column Visibility Popover */}
        <Popover
          open={Boolean(anchorEl)}
          anchorEl={anchorEl}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          <List dense sx={{ minWidth: 250 }}>
            {columnOrder.map((column, index) => (
              <ListItem key={column} sx={{ py: 0.5 }}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  width: '100%'
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Checkbox
                      edge="start"
                      checked={visibleColumns.has(column)}
                      onChange={() => handleToggleColumn(column)}
                      tabIndex={-1}
                      disableRipple
                      sx={{ mr: 1.5 }}
                    />
                    <ListItemText primary={column} />
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <IconButton
                      size="small"
                      disabled={index === 0}
                      onClick={() => handleMoveColumn(column, 'up')}
                      sx={{ p: 0.5, mb: 0.5 }}
                    >
                      <KeyboardArrowUpIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      disabled={index === columnOrder.length - 1}
                      onClick={() => handleMoveColumn(column, 'down')}
                      sx={{ p: 0.5 }}
                    >
                      <KeyboardArrowDownIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        </Popover>

        {/* Table Container */}
        <Box sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            border: '1px solid #e0e0e0',
            borderRadius: 1,
            width: '100%',
            position: 'relative',
          }}>
          <TableContainer 
            sx={{
              flex: 1,
              overflow: 'auto',
              position: 'relative',
              width: '100%',
              '& .MuiTable-root': { 
                minWidth: '100%',
                tableLayout: 'fixed',
              },
            }}
          >
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ 
                    width: 150, 
                    minWidth: 150,
                    maxWidth: 150,
                    backgroundColor: 'background.paper'
                  }}>Actions</TableCell>
                  {columnOrder.map(column => visibleColumns.has(column) && (
                    <TableCell 
                      key={column}
                      sx={{
                        width: columnWidth,
                        minWidth: 200,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        backgroundColor: 'background.paper'
                      }}
                    >
                      <TableSortLabel
                        active={orderBy === column}
                        direction={orderBy === column ? sortDirection : 'asc'}
                        onClick={() => handleSort(column)}
                        sx={{
                          width: '100%',
                          '&:hover': {
                            color: 'primary.main',
                          }
                        }}
                      >
                        {column}
                      </TableSortLabel>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={index} hover>
                      <TableCell sx={{ 
                          width: 150, 
                          minWidth: 150,
                          maxWidth: 150
                        }}>
                        <Button
                          component={Link}
                          to={`/orgs/${row.org_id}`}
                          variant="outlined"
                          color="default"
                          fullWidth
                          sx={{
                            py: 1,
                            borderRadius: '8px',
                            textTransform: 'none',
                            border: '1px solid #ddd',
                            fontSize: '12px',
                            '&:hover': {
                              backgroundColor: '#f0f0f0',
                              borderColor: '#bbb',
                            },
                          }}
                        >
                          View Details
                        </Button>
                      </TableCell>
                      {columnOrder.map(column => visibleColumns.has(column) && (
                        <TableCell 
                          key={column}
                          sx={{
                            width: columnWidth,
                            minWidth: 200,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                          }}
                        >{row[column] || '-'}</TableCell>
                      ))}
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pagination */}
          <TablePagination
            rowsPerPageOptions={[25, 50, 100]}
            component="div"
            count={sortedData.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              borderTop: '1px solid #e0e0e0',
              bgcolor: 'background.paper',
              position: 'sticky',
              bottom: 0,
              zIndex: 2
            }}
          />
        </Box>
      </Box>
        )}
    </Layout>
  );
};

export default OrgsTable;